import { cloneDeep } from "lodash";
import api from "../api";

export const VersionsStore = {
  namespaced: true,

  state: {
    isRevettingStarted: false, // TODO Save to LocalStorage || API field
    isComparisonMode: false,
    timelinePoints: [],
    comparedFormsData: null,
    refreshConfig: null,
    nextRefreshDate: null,
    loadingState: {
      timeline: false,
      versions: false,
    },
  },

  getters: {
    getIsRevettingStarted: state=> state.isRevettingStarted,
    getRefreshConfig: state=> state.refreshConfig,
    getNextRefreshDate: state=> state.nextRefreshDate,
    getIsComparisonMode: state => state.isComparisonMode,
    getTimelinePoints: state => {
      return state.timelinePoints.map(el => ({ ...el, current: el.current ?? false }));
    },
    getSelectedPoints: state => state.timelinePoints.filter(el => el.current) ?? [],
    getComparedFormsData: state => {
      return Object.values(state.comparedFormsData ?? {});
    },
    getIsComparisonBuilderShown: state => {
      return state.isComparisonMode && state.comparedFormsData && !state.loadingState.versions;
    },
    getIsRefreshConfigCreated: state => Boolean(state?.refreshConfig?.id),
    getIsTimelineLoading: state => state.loadingState.timeline,
    getIsVersionsLoading: state => state.loadingState.versions,
  },

  mutations: {
    SET_TIMELINE_POINTS(state, payload) {
      state.timelinePoints = payload;
    },
    SET_COMPARED_FORMS_DATA(state, payload) {
      state.comparedFormsData = payload;
    },
    SELECT_POINT(state, payload) {
      state.selectedPoints = payload;
    },
    SET_COMPARISON_MODE(state, payload) {
      state.isComparisonMode = payload;
    },
    SET_LOADING(state, { key, value }) {
      state.loadingState[key] = value;
    },
    SET_REVETTING_STARTED(state, payload) {
      state.isRevettingStarted = payload;
    },
    SET_REFRESH_CONFIG(state, payload) {
      state.refreshConfig = payload;
    },
    SET_NEXT_REFRESH_DATE(state, payload) {
      state.nextRefreshDate = payload;
    },
  },

  actions: {
    fetchTimelinePoints: async ({ commit }, payload) => {
      commit("SET_LOADING", { key: "timeline", value: true });

      try {
        const data = await api.fetchRevettingTimeline(payload);
        if (data) {
          commit("SET_TIMELINE_POINTS", data)
        }
      } catch (error) {
        console.error("Error fetching revetting timeline:", error);
      } finally {
        commit("SET_LOADING", { key: "timeline", value: false });
      }
    },

    fetchComparedForms: async ({ commit }, payload) => {
      commit("SET_LOADING", { key: "versions", value: true });

      try {
        const data = await api.fetchComparedForms(payload);
        if (data) {
          commit("SET_COMPARED_FORMS_DATA", data)
        }
      } catch (error) {
        console.error("Error fetching revetting timeline:", error);
      } finally {
        commit("SET_LOADING", { key: "versions", value: false });
      }
    },

    fetchRefreshConfiguration: async ({ commit }, caseId) => {
      try {
        const data = await api.getRefreshConfiguration(caseId);
        if (data) {
          commit("SET_REFRESH_CONFIG", data);
        }
      } catch (error) {
        console.error("Error on fetching revetting timeline:", error);
      }
    },

    createRefreshConfiguration: async (_, payload) => {
      try {
        const data = await api.postRefreshConfiguration(payload);
        return data;
      } catch (error) {
        console.error("Error on creating refresh config:", error);
      }
    },

    updateRefreshConfiguration: async ({ state }, payload) => {
      try {
        const data = await api.putRefreshConfiguration(state.refreshConfig.id, payload);
        return data;
      } catch (error) {
        console.error("Error on updating refresh config:", error);
      }
    },

    fetchNextRefreshDate: async ({ commit, state, getters }) => {
      if (!getters.getIsRefreshConfigCreated) return;

      try {
        const data = await api.getNextRefreshDate(state.refreshConfig.id);
        if (data) {
          commit("SET_NEXT_REFRESH_DATE", data.next_refresh_date);
        }
      } catch (error) {
        console.error("Error on fetching next refresh date:", error);
      }
    },

    startRevetting: async ({ commit }, case_id) => {
      try {
        const response = await api.startCaseRevetting(case_id);
        commit("SET_REVETTING_STARTED", true);
        return response;
      } catch (error) {
        console.error("Error starting revetting:", error);
      }
    },

    stopRevetting: async ({ commit }, case_id) => {
      try {
        const response = await api.stopCaseRevetting(case_id);
        commit("SET_REVETTING_STARTED", false);
        return response;
      } catch (error) {
        console.error("Error stopping revetting:", error);
      }
    },

    selectPoint: ({ commit, state }, payload) => {
      const pointIndex = state.timelinePoints.findIndex(el => el?.id === payload.id) ?? {};
      const clone = cloneDeep(state.timelinePoints);
      clone[pointIndex].current = !clone[pointIndex].current;

      commit("SET_TIMELINE_POINTS", clone);
    },

    clearSelectedPoints({ commit, state }) {
      const clone = cloneDeep(state.timelinePoints);
      for (const point of clone) {
        point.current = false;
      }
      commit("SET_TIMELINE_POINTS", clone);
    },

    resetState: ({ commit, dispatch }) => {
      commit("SET_COMPARISON_MODE", false);
      dispatch("clearSelectedPoints");
    },

    clearState: ({ commit }) => {
      commit("SET_COMPARISON_MODE", false);
      commit("SET_TIMELINE_POINTS", []);
      commit("SET_COMPARED_FORMS_DATA", null);
    },
  },
};
